import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Database, ExternalLink, ArrowLeft, Shield, Globe, RefreshCw } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import LanguageSelector from '@/components/LanguageSelector';
import euSearchIcon from "@/assets/eu-search-icon.png";

const DataSource = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Data Source | Official EU Tender Data - TED Monitor"
        description="TED Monitor exclusively uses official data from Tenders Electronic Daily (TED), the European Union's official platform for public procurement notices."
        path="/data-source"
        alternates={[
          { lang: 'en', href: '/data-source' },
          { lang: 'it', href: '/it/fonte-dei-dati' }
        ]}
      />
      
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b bg-card/80 backdrop-blur-md sticky top-0 z-50">
          <div className="container mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 group cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md shadow-primary/25 group-hover:shadow-primary/40 transition-shadow overflow-hidden">
                <img src={euSearchIcon} alt="EU Search" className="w-6 h-6 object-contain" />
              </div>
              <span className="text-base font-bold text-foreground">TED Monitor</span>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="h-8 text-xs">
                <ArrowLeft className="w-3 h-3 mr-1" />
                Back
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12 max-w-3xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-4">
              <Database className="w-8 h-8" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Official Data Source
            </h1>
            <p className="text-muted-foreground">
              Transparency and reliability in European public procurement data
            </p>
          </div>

          {/* Main Card */}
          <Card className="border-border/50 bg-card/50 backdrop-blur-sm mb-6">
            <CardContent className="p-6 md:p-8">
              <div className="space-y-6">
                {/* Data Origin Section */}
                <div>
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    Data Origin
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    TED Monitor is built exclusively on data published on <strong>Tenders Electronic Daily (TED)</strong>, 
                    the European Union's official platform for public procurement notices. 
                    TED is managed by the Publications Office of the European Union and represents the most 
                    authoritative and comprehensive source for European public tenders above EU thresholds.
                  </p>
                </div>

                {/* Update Frequency Section */}
                <div className="border-t border-border/50 pt-6">
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-primary" />
                    Data Updates
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Data is synchronized daily with the TED database, ensuring access to the most recent 
                    tender information. Our platform automatically processes and indexes new publications 
                    to enable fast and accurate searches.
                  </p>
                </div>

                {/* Independence Section */}
                <div className="border-t border-border/50 pt-6">
                  <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Service Independence
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    TED Monitor is an independent service and is not affiliated with, sponsored by, or endorsed 
                    by the European Union or the Publications Office. We provide advanced search, monitoring, 
                    and analysis tools to facilitate access to procurement opportunities, but the primary 
                    data source always remains the official TED platform.
                  </p>
                </div>

                {/* Official Source Link */}
                <div className="border-t border-border/50 pt-6">
                  <p className="text-sm text-muted-foreground mb-3">
                    Visit the official data source:
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => window.open('https://ted.europa.eu', '_blank')}
                    className="gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    ted.europa.eu
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <p className="text-xs text-muted-foreground text-center">
            TED Monitor is not responsible for any errors or omissions in data from the original source. 
            We recommend always verifying information on the official TED platform before taking any action.
          </p>
        </main>
      </div>
    </>
  );
};

export default DataSource;
